import PropTypes from "prop-types";
import React, { useState } from "react";
import { Badge, Button, Card, Col, Container, Nav, Row } from "react-bootstrap";

// Components
import { OptimizedImage } from "../../Common";

export const PuppyHero = ({ puppy, onSectionChange, activeSection }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  // Get the hero image from carouselOptimized or fallback to first carousel image
  const heroImage =
    puppy.carouselOptimized && puppy.carouselOptimized.length > 0
      ? puppy.carouselOptimized[0]
      : puppy.carousel && puppy.carousel.length > 0
        ? puppy.carousel[0]
        : puppy.image;

  const getAvailabilityInfo = () => {
    if (puppy.available) {
      return {
        status: "✅ Available Now",
        className: "available",
        urgency: "🔥 Ready for immediate adoption - Reserve today!",
        variant: "success",
      };
    } else {
      return {
        status: "Sold",
        className: "sold",
        urgency:
          "This puppy has found their forever home - Join our waiting list for the next litter",
        variant: "secondary",
      };
    }
  };

  const availabilityInfo = getAvailabilityInfo();

  const sections = [
    { key: "overview", label: "Overview" },
    { key: "training", label: "Training Progress" },
    { key: "personality", label: "Personality Profile" },
    { key: "gallery", label: "Gallery" },
    { key: "investment", label: "Investment" },
  ];

  return (
    <div className="puppy-hero">
      <Container fluid className="hero-container">
        <Row className="hero-content">
          {/* Hero Image Section */}
          <Col lg={7} className="hero-image-section">
            <div className="hero-image-container">
              <OptimizedImage
                src={heroImage}
                alt={`${puppy.cardHeaderTitle} - Professional trained puppy`}
                className={`hero-image ${imageLoaded ? "loaded" : "loading"}`}
                onLoad={() => setImageLoaded(true)}
                size="large"
              />

              {/* Availability Badge */}
              <Badge
                className={`availability-badge ${availabilityInfo.className}`}
                bg={availabilityInfo.variant}
              >
                {availabilityInfo.status}
              </Badge>

              {/* Quick Action Buttons */}
              <div className="hero-actions">
                <Button
                  variant="primary"
                  size="lg"
                  className="cta-primary"
                  href="sms:+13179546952?body=Hi Justine! I'd like to schedule a consultation about one of your trained puppies."
                >
                  Schedule Consultation
                </Button>
              </div>
            </div>
          </Col>

          {/* Hero Info Section */}
          <Col lg={5} className="hero-info-section">
            <div className="hero-info-content">
              {/* Main Title */}
              <div className="hero-title-section">
                <h1 className="puppy-name">{puppy.cardHeaderTitle}</h1>
                <div className="puppy-subtitle">
                  <span className="breed">
                    Professional Trained {puppy.breedLong || puppy.breed}
                  </span>
                  <div className="availability-urgency">
                    {availabilityInfo.urgency}
                  </div>
                </div>
              </div>

              {/* Navigation Pills */}
              <Nav variant="pills" className="section-navigation">
                {sections.map((section) => (
                  <Nav.Item key={section.key}>
                    <Nav.Link
                      active={activeSection === section.key}
                      onClick={() => onSectionChange(section.key)}
                      className="section-nav-pill"
                    >
                      <span className="nav-label">{section.label}</span>
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>

              {/* Quick Stats Bar */}
              <Card className="quick-stats-card">
                <Card.Body>
                  <Row className="quick-stats">
                    {puppy.sex && (
                      <Col xs={6} sm={3} className="stat-item">
                        <div className="stat-label">Sex</div>
                        <div className="stat-value">{puppy.sex}</div>
                      </Col>
                    )}
                    {puppy.dob && (
                      <Col xs={6} sm={3} className="stat-item">
                        <div className="stat-label">Date of Birth</div>
                        <div className="stat-value">
                          {new Date(puppy.dob).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </div>
                      </Col>
                    )}
                    {puppy.projectedSize && (
                      <Col xs={6} sm={3} className="stat-item">
                        <div className="stat-label">Projected Size</div>
                        <div className="stat-value">{puppy.projectedSize}</div>
                      </Col>
                    )}
                    <Col xs={6} sm={3} className="stat-item">
                      <div className="stat-label">Training Status</div>
                      <div className="stat-value">Complete</div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Available Date */}
              {puppy.available && puppy.availableDate && (
                <div className="availability-info">
                  <span className="availability-label">Available:</span>
                  <span className="availability-date">
                    {puppy.availableDate}
                  </span>
                </div>
              )}

              {/* Price Preview */}
              {puppy.price && puppy.price !== "unavailable" && (
                <div className="price-preview">
                  <div className="price-label">Investment</div>
                  <div className="price-value">{puppy.price}</div>
                  <div className="price-subtitle">
                    Fully trained & health guaranteed
                  </div>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

PuppyHero.propTypes = {
  puppy: PropTypes.object.isRequired,
  onSectionChange: PropTypes.func.isRequired,
  activeSection: PropTypes.string.isRequired,
};
